import { Component, forwardRef, Input } from '@angular/core';
import { FieldWorkerLink, CustomerClient } from 'src/shared/api';
import {
	FormControl,
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
	takeUntil,
	map,
	filter,
	distinctUntilChanged,
	skipWhile,
} from 'rxjs/operators';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	displayName,
	idComparer,
	touchControl,
} from 'src/shared/utilities/utility-functions';
import { FormConfig } from '../form-cva.component';
import { TypeAheadService } from 'src/shared/services/type-ahead.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

export const fieldWorkerLinkFormConfig: FormConfig<FieldWorkerLink> = {
	id: [''],
	name: [''],
	firstName: [''],
	lastName: [''],
	employeeCode: [''],
	paycomId: [''],
	jobTitle: [''],
	payrollType: [''],
	mealBreakAttestationSigned: [''],
	memberOfFieldTeamId: [''],
	language: [''],
};

@Component({
	selector: 'rpshared-field-worker-link',
	templateUrl: './field-worker-link.component.html',
	styleUrls: ['./field-worker-link.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FieldWorkerLinkComponent),
			multi: true,
		},
	],
})
export class FieldWorkerLinkComponent
	extends SelfDestruct
	implements ControlValueAccessor
{
	@Input() placeholder = 'Field Worker';
	private _required = false;
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}
	initialValue: FieldWorkerLink;
	fieldWorker = new FormControl('') as FormControl;

	readonly displayName = displayName;
	readonly idComparer = idComparer;

	filteredFieldWorkers$ = this.typeAheadService.filteredFieldWorkers$(
		this.fieldWorker.valueChanges
	);

	constructor(
		private readonly customerClient: CustomerClient,
		private readonly typeAheadService: TypeAheadService
	) {
		super();
	}

	// model --> view
	writeValue(value: FieldWorkerLink | number): void {
		if (typeof value === 'number') {
			this.customerClient.getFieldWorkerLink(value).subscribe((fw) => {
				this.initialValue = fw;
				this.fieldWorker.setValue(fw, { emitEvent: false });
			});
		} else if (value) {
			this.initialValue = value;
			this.fieldWorker.setValue(value);
		} else {
			this.fieldWorker.reset('', { emitEvent: false });
		}
	}

	// view --> model
	registerOnChange(fn: (value: FieldWorkerLink | number) => void) {
		this.fieldWorker.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				distinctUntilChanged(),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.fieldWorker)(fn);
	}

	onTouched: () => void = () => {};
}

@Component({
	selector: 'rpshared-field-worker-id',
	templateUrl: './field-worker-link.component.html',
	styleUrls: ['./field-worker-link.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FieldWorkerIdComponent),
			multi: true,
		},
	],
})
export class FieldWorkerIdComponent extends FieldWorkerLinkComponent {
	registerOnChange(fn: (value: FieldWorkerLink | number) => void) {
		this.fieldWorker.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				map((v) => (v ? v.id : null)),
				distinctUntilChanged(),
				skipWhile<number | null>((v) => !v && !this.initialValue),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
}
